import type { DiagnosticSections, DiagnosticSnapshot } from "@/api/diagnostic";
import type { components } from "@/types/api.generated";

import {
  closedPercents,
  countMap,
  num,
  rec,
  wricefLetterColumns,
  type Destination,
} from "../diagnosticData";

type Validation = DiagnosticSnapshot["validation"];

/** As quatro decisões do arquiteto, como a F1 as grava. */
export type WricefDecisionToken = NonNullable<
  components["schemas"]["WricefDecisionItem"]["decision"]
>;

/**
 * O balde de quem ainda não tem decisão, ou estratégia sugerida — o mesmo
 * texto que o domínio ``wricef`` do backend usa
 * (``wricef_decision_domain.py``), para o card (F5) e a legenda falarem do
 * mesmo jeito.
 */
export const UNDECIDED = "_UNDECIDED";
export const UNKNOWN_STRATEGY = "_UNKNOWN";

export type DecisionOrUndecided = WricefDecisionToken | typeof UNDECIDED;

/** A ordem do mockup da EF: em escopo, pendente, fora, standard e sem decisão.
 * O card (F5) reaproveita esta mesma ordem na legenda. */
export const DECISION_ORDER: readonly DecisionOrUndecided[] = [
  "in_scope",
  "client_validation",
  "out_of_scope",
  "covered_by_standard",
  UNDECIDED,
];

/** Do mais limpo ao menos limpo — a mesma ordem de `TARGET_ORDER` em `diagnosticMeta.ts`. */
const STRATEGY_ORDER = ["key_user", "side_by_side", "developer", "classic"] as const;

export interface WricefDecisionSegment {
  decision: DecisionOrUndecided;
  value: number;
}

export interface WricefDecisionColumn {
  letter: string;
  total: number;
  segments: WricefDecisionSegment[];
}

export interface WricefStrategySlice {
  strategy: string;
  count: number;
  hours: number;
}

export interface WricefInScopeBreakdown {
  count: number;
  hours: number;
  missingHours: number;
  slices: WricefStrategySlice[];
}

export interface WricefDecisionFrame {
  universe: number;
  decided: number;
  undecided: number;
  byDecision: Record<WricefDecisionToken, number>;
  columns: WricefDecisionColumn[];
  inScope: WricefInScopeBreakdown | undefined;
}

/**
 * Os segmentos de uma coluna, na ordem da EF. Token desconhecido entra no
 * fim, ordenado — o mesmo tratamento que `wricefLetterColumns` já dá a letra
 * fora do vocabulário conhecido (`diagnosticData.ts:416`).
 */
function orderedSegments(bucket: Record<string, number>): WricefDecisionSegment[] {
  const declared = DECISION_ORDER.filter((token) => token in bucket).map((token) => ({
    decision: token,
    value: bucket[token],
  }));
  const extra = Object.keys(bucket)
    .filter((token) => !(DECISION_ORDER as readonly string[]).includes(token))
    .sort()
    .map((token) => ({ decision: token as DecisionOrUndecided, value: bucket[token] }));
  return [...declared, ...extra];
}

function strategyBucket(value: unknown): { count: number; hours: number } {
  const bucket = rec(value);
  return { count: num(bucket.count) ?? 0, hours: num(bucket.hours) ?? 0 };
}

/** As fatias do bloco "em escopo", na mesma ordem de `TARGET_ORDER`, com o
 * balde de estratégia desconhecida sempre no fim. */
function strategySlices(byStrategy: Record<string, unknown>): WricefStrategySlice[] {
  const declared = STRATEGY_ORDER.filter((strategy) => strategy in byStrategy).map((strategy) => ({
    strategy,
    ...strategyBucket(byStrategy[strategy]),
  }));
  const extra = Object.keys(byStrategy)
    .filter((strategy) => strategy !== UNKNOWN_STRATEGY)
    .filter((strategy) => !(STRATEGY_ORDER as readonly string[]).includes(strategy))
    .sort()
    .map((strategy) => ({ strategy, ...strategyBucket(byStrategy[strategy]) }));
  const unknown =
    UNKNOWN_STRATEGY in byStrategy
      ? [{ strategy: UNKNOWN_STRATEGY, ...strategyBucket(byStrategy[UNKNOWN_STRATEGY]) }]
      : [];
  return [...declared, ...extra, ...unknown];
}

/**
 * O frame pronto para o card desenhar, ou `undefined` se o projeto ainda não
 * tem nenhuma decisão registrada — é o que mantém a classificação automática
 * na tela (D5, #1343).
 */
export function wricefDecisions(validation: Validation | undefined): WricefDecisionFrame | undefined {
  const envelope = validation?.wricef;
  if (!envelope || envelope.state !== "ok") return undefined;

  const data = rec(envelope.data);
  const decided = num(data.decided) ?? 0;
  if (decided === 0) return undefined;

  const byDecision = countMap(data.by_decision) as Record<WricefDecisionToken, number>;
  const byWricefDecision = rec(data.by_wricef_decision);
  const columns: WricefDecisionColumn[] = Object.entries(byWricefDecision).map(([letter, bucket]) => {
    const segments = orderedSegments(countMap(bucket));
    const total = segments.reduce((acc, segment) => acc + segment.value, 0);
    return { letter, total, segments };
  });

  const inScopeByStrategy = rec(data.in_scope_by_strategy);
  const inScope: WricefInScopeBreakdown | undefined =
    Object.keys(inScopeByStrategy).length === 0
      ? undefined
      : {
          count: byDecision.in_scope ?? 0,
          hours: num(data.in_scope_hours) ?? 0,
          missingHours: num(data.in_scope_missing_hours) ?? 0,
          slices: strategySlices(inScopeByStrategy),
        };

  return {
    universe: num(data.universe) ?? 0,
    decided,
    undecided: num(data.undecided) ?? 0,
    byDecision,
    columns,
    inScope,
  };
}

/** As estratégias que não tocam o núcleo: key user e developer in-app, e side-by-side no BTP. */
const CLEAN_STRATEGIES: readonly string[] = ["key_user", "developer", "side_by_side"];

/**
 * O destino do código Z no Ato 2 pela decisão do arquiteto (#1712, cartão 3.7): as
 * quatro decisões e o "sem decisão", sobre o mesmo universo do card WRICEF do Ato
 * 1. O Marco decidiu em 22/10/2026 que a classificação automática fica só no
 * Architect, e que os dois cards contam a mesma coisa.
 */
export function decisionDestination(frame: WricefDecisionFrame): Destination {
  const counts: Record<string, number> = { ...frame.byDecision, [UNDECIDED]: frame.undecided };
  const values = DECISION_ORDER.map((token) => counts[token] ?? 0);
  // Fechados em 100: a barra do Ato 2 e a do Resumo leem os mesmos números.
  const pcts = frame.universe ? closedPercents(values, frame.universe) : undefined;
  return {
    segments: DECISION_ORDER.map((token, index) => ({
      key: token,
      count: values[index],
      pct: pcts?.[index],
    })),
    total: frame.universe,
  };
}

/** Os objetos em escopo que vão para extensão que não modifica o núcleo. */
export function cleanExtensionCount(frame: WricefDecisionFrame): number {
  return (frame.inScope?.slices ?? [])
    .filter((slice) => CLEAN_STRATEGIES.includes(slice.strategy))
    .reduce((sum, slice) => sum + slice.count, 0);
}

/**
 * Os desenvolvimentos executáveis (#1476): o universo da decisão do arquiteto
 * quando ela existe, e senão a soma das letras do catálogo. O Ato 1 e o Resumo
 * leem este mesmo número.
 */
export function developmentsAnalyzed(
  sections: DiagnosticSections,
  validation: Validation | undefined,
): number | undefined {
  const frame = wricefDecisions(validation);
  if (frame) return frame.universe;
  // Os baldes que não são letra (`_NOT_APPLICABLE`, `_UNKNOWN`) ficam fora, como
  // nas barras do card: classe e include são parte de um entregável, e não um.
  const columns = wricefLetterColumns(sections).filter((column) => !column.letter.startsWith("_"));
  if (columns.length === 0) return undefined;
  return columns.reduce((acc, column) => acc + column.total, 0);
}
